import React, { useState, useMemo } from 'react';
import { Card, Table, Tabs, Statistic, Row, Col, Tag, Progress, Tooltip, Space, Button } from 'antd';
import { DownloadOutlined, BarChartOutlined, HeatMapOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as echarts from 'echarts';
import { useEffect, useRef } from 'react';

const { TabPane } = Tabs;

// 统一的颜色方案
const PALETTE = {
  stabilizing: '#1890ff',      // 蓝色：ΔΔG < 0 稳定化
  neutral: '#52c41a',          // 绿色：ΔΔG = 0 中性
  destabilizing: '#ff4d4f',    // 红色：ΔΔG > 0 不稳定化
  text: '#222'
};

// 序列查看器组件
interface SequenceViewerProps {
  mutations: ThermoMPNNMutation[];
  onPositionClick: (position: number) => void;
}

const SequenceViewer: React.FC<SequenceViewerProps> = ({ mutations, onPositionClick }) => {
  // 从突变数据中提取原始序列信息
  const sequenceData = useMemo(() => {
    const positionMap = new Map<number, { wildtype: string, mutationCount: number }>();
    
    mutations.forEach(mutation => {
      if (!positionMap.has(mutation.position)) {
        positionMap.set(mutation.position, {
          wildtype: mutation.wildtype,
          mutationCount: 0
        });
      }
      positionMap.get(mutation.position)!.mutationCount++;
    });
    
    return Array.from(positionMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([position, data]) => ({
        position,
        amino: data.wildtype,
        mutationCount: data.mutationCount
      }));
  }, [mutations]);

  return (
    <Card size="small" title="蛋白质原始序列">
      <div style={{ 
        fontFamily: 'monospace', 
        fontSize: '14px', 
        lineHeight: '2',
        padding: '16px',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
        maxHeight: '300px',
        overflowY: 'auto'
      }}>
        {sequenceData.map((item) => (
          <Tooltip key={item.position} title={`位点 ${item.position}: ${item.amino}`}>
            <span
              style={{
                margin: '2px',
                padding: '4px 6px',
                backgroundColor: '#ffffff',
                color: '#222',
                border: '1px solid #d9d9d9',
                borderRadius: '3px',
                cursor: 'pointer',
                fontWeight: 600
              }}
              onClick={() => onPositionClick(item.position)}
            >
              {item.amino}
            </span>
          </Tooltip>
        ))}
      </div>
    </Card>
  );
};

interface ThermoMPNNMutation {
  position: number;
  wildtype: string;
  mutation: string;
  ddG_pred: number;
  mutation_name: string;
  category: string;
}

interface ThermoMPNNPositionAnalysis {
  position: number;
  wildtype: string;
  mean_ddG: number;
  min_ddG: number;
  max_ddG: number;
  std_ddG: number;
  best_mutation: string;
  best_ddG: number;
}

interface ThermoMPNNSummary {
  total_mutations: number;
  mean_ddG: number;
  std_ddG: number;
  best_mutation: {
    mutation: string;
    ddG: number;
    position: number;
    wildtype: string;
    mutant: string;
  };
  worst_mutation: {
    mutation: string;
    ddG: number;
    position: number;
    wildtype: string;
    mutant: string;
  };
  stabilizing_count: number;
  neutral_count: number;
  destabilizing_count: number;
}

interface ThermoMPNNResultData {
  summary?: ThermoMPNNSummary;
  mutations: ThermoMPNNMutation[];
  position_analysis?: ThermoMPNNPositionAnalysis[];
  files?: {
    input_pdb: string;
    output_csv: string;
    task_directory: string;
  };
  csv_data?: any[];
  // 统一格式字段
  total_sequences?: number;
  successful_count?: number;
  failed_count?: number;
  results?: Array<{
    sequence_id: string;
    result: {
      summary?: ThermoMPNNSummary;
      mutations: ThermoMPNNMutation[];
      position_analysis?: ThermoMPNNPositionAnalysis[];
      files?: any;
      task_id?: string;
      total_mutations?: number;
      csv_data?: any[];
    };
    success: boolean;
    error?: string;
  }>;
  artifacts?: any;
  meta?: any;
}

interface ThermoMPNNResultsProps {
  taskResult: {
    results: Array<{
      sequence_id: string;
      result: ThermoMPNNResultData;
      success: boolean;
      error?: string;
    }>;
    artifacts?: {
      files?: any;
      csv_data?: any[];
    };
  };
}

const ThermoMPNNResults: React.FC<ThermoMPNNResultsProps> = ({ taskResult }) => {
  const [activeTab, setActiveTab] = useState('summary');
  const heatmapRef = useRef<HTMLDivElement>(null);
  const distributionRef = useRef<HTMLDivElement>(null);

  console.log('ThermoMPNN Debug - taskResult:', taskResult);
  const resultData = taskResult.results?.[0]?.result;
  console.log('ThermoMPNN Debug - resultData:', resultData);
  
  // 从统一格式中提取实际的ThermoMPNN数据
  const thermoMPNNData = resultData?.results?.[0]?.result;
  console.log('ThermoMPNN Debug - thermoMPNNData:', thermoMPNNData);
  
  if (!thermoMPNNData || !resultData?.results?.[0]?.success) {
    return (
      <Card title="ThermoMPNN 稳定性预测结果">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>暂无结果数据或分析失败</p>
          {resultData?.results?.[0]?.error && (
            <p style={{ color: 'red' }}>错误: {resultData.results[0].error}</p>
          )}
        </div>
      </Card>
    );
  }

  const { summary, mutations: maybeMutations, position_analysis, files } = thermoMPNNData;
  console.log('ThermoMPNN Debug - summary:', summary);
  console.log('ThermoMPNN Debug - maybeMutations:', maybeMutations);
  console.log('ThermoMPNN Debug - position_analysis:', position_analysis);
  const mutations: ThermoMPNNMutation[] = Array.isArray(maybeMutations) ? maybeMutations : [];
  console.log('ThermoMPNN Debug - mutations array:', mutations, 'length:', mutations.length);
  const safeSummary: ThermoMPNNSummary = summary || {
    total_mutations: Array.isArray(mutations) ? mutations.length : 0,
    mean_ddG: 0,
    std_ddG: 0,
    best_mutation: { mutation: '-', ddG: 0, position: 0, wildtype: '-', mutant: '-' },
    worst_mutation: { mutation: '-', ddG: 0, position: 0, wildtype: '-', mutant: '-' },
    stabilizing_count: 0,
    neutral_count: 0,
    destabilizing_count: 0,
  };
  const safePositionAnalysis: ThermoMPNNPositionAnalysis[] = position_analysis || [];
  const safeFiles = files || { input_pdb: '', output_csv: '', task_directory: '' };

  // Table pagination state and utilities
  const [mutationsPage, setMutationsPage] = useState(1);
  const [positionsPage, setPositionsPage] = useState(1);

  // Amino acid order for stable sorting and page locating
  const aminoAcidsOrder = ['A', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'K', 'L', 'M', 'N', 'P', 'Q', 'R', 'S', 'T', 'V', 'W', 'Y'];

  const sortedMutations: ThermoMPNNMutation[] = useMemo(() => {
    const arr = Array.isArray(mutations) ? [...mutations] : [];
    return arr.sort((a, b) => {
      if (a.position !== b.position) return a.position - b.position;
      return aminoAcidsOrder.indexOf(a.mutation) - aminoAcidsOrder.indexOf(b.mutation);
    });
  }, [mutations]);

  // Handle sequence click: jump page within current tab
  const handleSequenceClick = (position: number) => {
    if (activeTab === 'mutations') {
      const firstIndex = sortedMutations.findIndex(m => m.position === position);
      const page = firstIndex >= 0 ? Math.floor(firstIndex / 20) + 1 : 1;
      setMutationsPage(page);
    } else if (activeTab === 'positions') {
      const posIndex = safePositionAnalysis.findIndex(r => r.position === position);
      const page = posIndex >= 0 ? Math.floor(posIndex / 20) + 1 : 1;
      setPositionsPage(page);
    }
  };

  // 突变表格列定义
  const mutationColumns: ColumnsType<ThermoMPNNMutation> = [
    {
      title: '位点',
      dataIndex: 'position',
      key: 'position',
      width: 80,
      sorter: (a, b) => a.position - b.position,
    },
    {
      title: '野生型',
      dataIndex: 'wildtype',
      key: 'wildtype',
      width: 80,
      align: 'center',
    },
    {
      title: '突变型',
      dataIndex: 'mutation',
      key: 'mutation',
      width: 80,
      align: 'center',
    },
    {
      title: '突变名称',
      dataIndex: 'mutation_name',
      key: 'mutation_name',
      width: 100,
    },
    {
      title: 'ΔΔG (kcal/mol)',
      dataIndex: 'ddG_pred',
      key: 'ddG_pred',
      width: 140,
      sorter: (a, b) => a.ddG_pred - b.ddG_pred,
      render: (value: number) => (
        <span style={{ 
          color: value < 0 ? PALETTE.stabilizing : value > 0 ? PALETTE.destabilizing : PALETTE.neutral,
          fontWeight: 'bold'
        }}>
          {value.toFixed(3)}
        </span>
      ),
    },
    {
      title: '稳定性分类',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      filters: [
        { text: '稳定化', value: 'stabilizing' },
        { text: '中性', value: 'neutral' },
        { text: '不稳定', value: 'destabilizing' },
      ],
      onFilter: (value, record) => record.category === value,
      render: (category: string) => {
        const config = {
          stabilizing: { color: PALETTE.stabilizing, text: '稳定化' },
          neutral: { color: PALETTE.neutral, text: '中性' },
          destabilizing: { color: PALETTE.destabilizing, text: '不稳定化' }
        } as const;
        const item = config[category as keyof typeof config];
        return <Tag color={item?.color || PALETTE.text}>{item?.text || category}</Tag>;
      },
    },
  ];

  // 位点分析表格列定义
  const positionColumns: ColumnsType<ThermoMPNNPositionAnalysis> = [
    {
      title: '位点',
      dataIndex: 'position',
      key: 'position',
      width: 80,
      sorter: (a, b) => a.position - b.position,
    },
    {
      title: '野生型',
      dataIndex: 'wildtype',
      key: 'wildtype',
      width: 80,
      align: 'center',
    },
    {
      title: '平均ΔΔG',
      dataIndex: 'mean_ddG',
      key: 'mean_ddG',
      width: 120,
      sorter: (a, b) => a.mean_ddG - b.mean_ddG,
      render: (value: number) => (
        <span style={{ color: value < 0 ? PALETTE.stabilizing : value > 0 ? PALETTE.destabilizing : PALETTE.neutral }}>
          {value.toFixed(3)}
        </span>
      ),
    },
    {
      title: '最小ΔΔG',
      dataIndex: 'min_ddG',
      key: 'min_ddG',
      width: 120,
      render: (value: number) => value.toFixed(3),
    },
    {
      title: '最大ΔΔG',
      dataIndex: 'max_ddG',
      key: 'max_ddG',
      width: 120,
      render: (value: number) => value.toFixed(3),
    },
    {
      title: '标准差',
      dataIndex: 'std_ddG',
      key: 'std_ddG',
      width: 100,
      sorter: (a, b) => a.std_ddG - b.std_ddG,
      render: (value: number) => value.toFixed(3),
    },
    {
      title: '最佳突变',
      dataIndex: 'best_mutation',
      key: 'best_mutation',
      width: 100,
      align: 'center',
    },
    {
      title: '最佳ΔΔG',
      dataIndex: 'best_ddG',
      key: 'best_ddG',
      width: 120,
      render: (value: number) => (
        <span style={{ color: value < 0 ? PALETTE.stabilizing : value > 0 ? PALETTE.destabilizing : PALETTE.neutral }}>
          {value.toFixed(3)}
        </span>
      ),
    },
  ];

  // 创建热图（仅在热图标签激活时初始化）
  useEffect(() => {
    console.log('Heatmap useEffect - mutations length:', mutations.length, 'activeTab:', activeTab);
    if (activeTab === 'heatmap' && heatmapRef.current && mutations.length > 0) {
      console.log('Initializing heatmap chart...');
      const chart = echarts.init(heatmapRef.current);
      
      // 准备热图数据
      const positions = Array.from(new Set(mutations.map(m => m.position))).sort((a, b) => a - b);
      const aminoAcids = ['A', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'K', 'L', 'M', 'N', 'P', 'Q', 'R', 'S', 'T', 'V', 'W', 'Y'];
      
      const heatmapData = mutations.map(m => [
        positions.indexOf(m.position),
        aminoAcids.indexOf(m.mutation),
        m.ddG_pred
      ]);

      // 统一色标：以0为中心，负为蓝、0为绿、正为红，强度按绝对值
      const ddgValues = mutations.map(m => m.ddG_pred);
      const ddgMin = Math.min(...ddgValues);
      const ddgMax = Math.max(...ddgValues);
      const maxAbs = Math.max(Math.abs(ddgMin), Math.abs(ddgMax));

      const option = {
        title: {
          text: 'ThermoMPNN 稳定性热图',
          left: 'center'
        },
        tooltip: {
          position: 'top',
          formatter: function(params: any) {
            const pos = positions[params.data[0]];
            const aa = aminoAcids[params.data[1]];
            const ddg = params.data[2];
            return `位点 ${pos}<br/>突变: ${aa}<br/>ΔΔG: ${ddg.toFixed(3)} kcal/mol`;
          }
        },
        grid: {
          height: '60%',
          top: '15%',
          left: '10%',
          right: '10%'
        },
        xAxis: {
          type: 'category',
          data: positions.map(p => p.toString()),
          splitArea: {
            show: true
          },
          name: '位点'
        },
        yAxis: {
          type: 'category',
          data: aminoAcids,
          splitArea: {
            show: true
          },
          name: '氨基酸'
        },
        visualMap: {
          min: ddgMin,
          max: ddgMax,
          calculable: true,
          orient: 'horizontal',
          left: 'center',
          bottom: 10,
          inRange: {
            color: [
              '#003a8c', // 深蓝色 (最负值)
              '#1890ff', // 蓝色 (负值)
              '#52c41a', // 绿色 (0值)
              '#ff7875', // 浅红色 (正值)
              '#a8071a'  // 深红色 (最正值)
            ]
          },
          text: ['高', '低'],
          textStyle: {
            color: '#333'
          }
        },
        series: [{
          name: 'ΔΔG',
          type: 'heatmap',
          data: heatmapData,
          label: {
            show: false
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }]
      };

      chart.setOption(option);
      // 触发一次自适应，避免首次进入时宽度为0
      setTimeout(() => chart.resize(), 0);
      return () => chart.dispose();
    }
  }, [mutations, activeTab]);

  // 创建分布图（仅在分布图标签激活时初始化）
  useEffect(() => {
    console.log('Distribution useEffect - mutations length:', mutations.length, 'activeTab:', activeTab);
    if (activeTab === 'distribution' && distributionRef.current && mutations.length > 0) {
      console.log('Initializing distribution chart...');
      const chart = echarts.init(distributionRef.current);
      
      const ddgValues = mutations.map(m => m.ddG_pred);
      
      const option = {
        title: {
          text: 'ΔΔG 值分布',
          left: 'center'
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        xAxis: {
          type: 'value',
          name: 'ΔΔG (kcal/mol)'
        },
        yAxis: {
          type: 'value',
          name: '频次'
        },
        series: [{
          name: '频次',
          type: 'bar',
          data: (() => {
            const bins = 20;
            const min = Math.min(...ddgValues);
            const max = Math.max(...ddgValues);
            const binSize = (max - min) / bins;
            const histogram = new Array(bins).fill(0);
            
            ddgValues.forEach(value => {
              const binIndex = Math.min(Math.floor((value - min) / binSize), bins - 1);
              histogram[binIndex]++;
            });
            
            return histogram.map((count, i) => [min + i * binSize + binSize / 2, count]);
          })(),
          itemStyle: {
            color: PALETTE.stabilizing
          }
        }]
      };

      chart.setOption(option);
      setTimeout(() => chart.resize(), 0);
      return () => chart.dispose();
    }
  }, [mutations, activeTab]);

  return (
    <Card title="ThermoMPNN 蛋白质稳定性预测结果" style={{ width: '100%' }}>
      {/* 原始序列始终显示在标题下方，便于联动查看 */}
      <SequenceViewer 
        mutations={mutations} 
        onPositionClick={handleSequenceClick}
      />

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="分析摘要" key="summary">
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Statistic 
                title="总突变数" 
                value={safeSummary.total_mutations} 
                suffix="个"
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="平均ΔΔG" 
                value={safeSummary.mean_ddG} 
                precision={3}
                suffix="kcal/mol"
                valueStyle={{ color: safeSummary.mean_ddG > 0 ? PALETTE.destabilizing : safeSummary.mean_ddG < 0 ? PALETTE.stabilizing : PALETTE.neutral }}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="标准差" 
                value={safeSummary.std_ddG} 
                precision={3}
                suffix="kcal/mol"
              />
            </Col>
            <Col span={6}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  icon={<DownloadOutlined />} 
                  onClick={() => {
                    const csvContent = mutations.map(m => 
                      `${m.position},${m.wildtype},${m.mutation},${m.ddG_pred},${m.category}`
                    ).join('\n');
                    const blob = new Blob([`Position,Wildtype,Mutation,ddG_pred,Category\n${csvContent}`], 
                      { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'thermompnn_results.csv';
                    a.click();
                  }}
                >
                  导出CSV
                </Button>
              </Space>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col span={8}>
              <Card size="small" title="稳定化突变">
                <Statistic 
                  value={safeSummary.stabilizing_count} 
                  suffix={`个 (${safeSummary.total_mutations ? ((safeSummary.stabilizing_count / safeSummary.total_mutations) * 100).toFixed(1) : '0.0'}%)`}
                  valueStyle={{ color: PALETTE.stabilizing }}
                />
                <Progress 
                  percent={safeSummary.total_mutations ? (safeSummary.stabilizing_count / safeSummary.total_mutations) * 100 : 0}
                  strokeColor={PALETTE.stabilizing}
                  showInfo={false}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" title="中性突变">
                <Statistic 
                  value={safeSummary.neutral_count} 
                  suffix={`个 (${safeSummary.total_mutations ? ((safeSummary.neutral_count / safeSummary.total_mutations) * 100).toFixed(1) : '0.0'}%)`}
                  valueStyle={{ color: PALETTE.neutral }}
                />
                <Progress 
                  percent={safeSummary.total_mutations ? (safeSummary.neutral_count / safeSummary.total_mutations) * 100 : 0}
                  strokeColor={PALETTE.neutral}
                  showInfo={false}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" title="不稳定突变">
                <Statistic 
                  value={safeSummary.destabilizing_count} 
                  suffix={`个 (${safeSummary.total_mutations ? ((safeSummary.destabilizing_count / safeSummary.total_mutations) * 100).toFixed(1) : '0.0'}%)`}
                  valueStyle={{ color: PALETTE.destabilizing }}
                />
                <Progress 
                  percent={safeSummary.total_mutations ? (safeSummary.destabilizing_count / safeSummary.total_mutations) * 100 : 0}
                  strokeColor={PALETTE.destabilizing}
                  showInfo={false}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col span={12}>
              <Card size="small" title="最稳定突变" style={{ borderColor: '#1890ff' }}>
                <p><strong>突变:</strong> {safeSummary.best_mutation.mutation}</p>
                <p><strong>位点:</strong> {safeSummary.best_mutation.position}</p>
                <p><strong>ΔΔG:</strong> <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                  {safeSummary.best_mutation.ddG.toFixed(3)} kcal/mol
                </span></p>
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small" title="最不稳定突变" style={{ borderColor: '#ff4d4f' }}>
                <p><strong>突变:</strong> {safeSummary.worst_mutation.mutation}</p>
                <p><strong>位点:</strong> {safeSummary.worst_mutation.position}</p>
                <p><strong>ΔΔG:</strong> <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                  {safeSummary.worst_mutation.ddG.toFixed(3)} kcal/mol
                </span></p>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="突变详情" key="mutations">
          <Table
            columns={mutationColumns}
            dataSource={sortedMutations}
            rowKey={(record) => `${record.position}-${record.mutation}`}
            pagination={{
              pageSize: 20,
              current: mutationsPage,
              onChange: (p) => setMutationsPage(p),
              showSizeChanger: false,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 个突变`
            }}
            scroll={{ y: 600 }}
            size="small"
          />
        </TabPane>

        <TabPane tab="位点分析" key="positions">
          <Table
            columns={positionColumns}
            dataSource={safePositionAnalysis}
            rowKey="position"
            pagination={{
              pageSize: 20,
              current: positionsPage,
              onChange: (p) => setPositionsPage(p),
              showSizeChanger: false,
              showTotal: (total) => `共 ${total} 个位点`
            }}
            scroll={{ y: 600 }}
            size="small"
          />
        </TabPane>

        <TabPane tab={<span><HeatMapOutlined />稳定性热图</span>} key="heatmap">
          <div ref={heatmapRef} style={{ width: '100%', height: '600px', overflowX: 'auto' }} />
        </TabPane>

        <TabPane tab={<span><BarChartOutlined />分布图</span>} key="distribution">
          <div ref={distributionRef} style={{ width: '100%', height: '500px' }} />
        </TabPane>

      </Tabs>
    </Card>
  );
};

export default ThermoMPNNResults;
